import React, {useId} from 'react';

import mergeRefs from 'merge-refs';

import {FlatButton} from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Scroll} from '@pb/kit-component-scroll';
import {useControlledState} from '@pb/kit-hooks-use-controlled-state';
import {hashedClasses, withCssModule} from '@pb/kit-util-with-css-module';

import {MoreLessButton} from './more-less-button';
import styles from './strip-of-pills.module.styl';
import {useHasOverflow} from './use-has-overflow';

import type {ComponentPropsWithoutRef, Ref} from 'react';

const classNames = withCssModule(styles);
const classes = hashedClasses(styles);

export type StripOfPillsGroupProps = ComponentPropsWithoutRef<'div'> & {
    /**
     * Short heading rendered above the pills (e.g. `"Filters (3)"`). The count
     * suffix is appended automatically from the `count` prop. Wired to
     * `aria-labelledby` on the `role="group"` wrapper.
     */
    label?: string,
    /**
     * Number appended to `label` as `(count)` in the caption. Has no effect
     * when `label` is omitted.
     */
    count?: number,
    /**
     * Called when the user clicks the "Clear All" button. Use to reset the
     * list of pills in the owning component's state.
     */
    onClear?: () => void,
    /**
     * Additional props forwarded to the "Clear All" `<FlatButton>`. Use to
     * override `children`, `className`, or `disabled`.
     */
    clearButtonProps?: ComponentPropsWithoutRef<typeof FlatButton>,
    /**
     * When `true` (default), renders the "Clear All" button. Set to `false`
     * to hide it (e.g. when the clear action is handled elsewhere).
     */
    showClearButton?: boolean,
    /**
     * Controls the collapsed state externally. When provided the component is
     * controlled — pair with `onChangeIsCollapsed` to update the value.
     * Defaults to `true` (collapsed on first render).
     */
    isCollapsed?: boolean,
    /**
     * Called when the user toggles the collapse state. Receives the new
     * `isCollapsed` boolean. Required when `isCollapsed` is controlled.
     */
    onChangeIsCollapsed?: (isCollapsed: boolean) => void,
    /** Ref forwarded to the pills container `<div>`. */
    pillContainerRef?: Ref<HTMLDivElement>,
    /** Ref forwarded to the buttons container `<span>` (More/Less + Clear All). */
    criteriaButtonsRef?: Ref<HTMLDivElement>,
    /**
     * When `true` (default), the "Show All / Show Less" toggle button is
     * rendered when the pills overflow the container. Set to `false` to always
     * show all pills without a toggle.
     */
    showCollapseButton?: boolean,
    /**
     * Label for the toggle button when the list is expanded. Defaults to
     * `"Show Less"`.
     */
    textExpanded?: string,
    /**
     * Label for the toggle button when the list is collapsed. Defaults to
     * `"Show All"`.
     */
    textCollapsed?: string,
};

/**
 * Scrollable group container that wraps a set of `<StripOfPills>` rows with a
 * shared label, item count, "Show All / Show Less" toggle, and an optional
 * "Clear All" button.
 *
 * The collapsed state is internally managed by default — pass `isCollapsed` and
 * `onChangeIsCollapsed` to control it from outside. When the pills overflow the
 * horizontal scroll area, a "Show All" button appears automatically regardless
 * of `showCollapseButton`.
 */
export const StripOfPillsGroup = ({
    label,
    children,
    count = 0,
    showClearButton = true,
    showCollapseButton = true,
    clearButtonProps,
    isCollapsed: isCollapsedProp,
    onChangeIsCollapsed: onChangeIsCollapsedProp,
    pillContainerRef,
    criteriaButtonsRef,
    onClear,
    textExpanded = 'Show Less',
    textCollapsed = 'Show All',
    ...props
}: StripOfPillsGroupProps) => {
    const scrollRef = React.useRef<HTMLDivElement | null>(null);
    const isOutOfBounds = useHasOverflow(scrollRef);

    const [isCollapsed, setCollapsed] = useControlledState(isCollapsedProp, onChangeIsCollapsedProp, true);
    const moreButton = showCollapseButton && (!isCollapsed || isOutOfBounds) ? (
        <MoreLessButton
            textExpanded={` ${textExpanded}`}
            textCollapsed={`${textCollapsed} `}
            isCollapsed={isCollapsed}
            onChange={setCollapsed}
        />
    ) : null;

    const clearButton = showClearButton ? (
        <>
            <span className={classes['strip-divider']}/>
            <FlatButton
                className={classes['inline-button']}
                onClick={onClear}
                startIcon={<Icon type={Icons.TRASH_LIGHT}/>}
                {...clearButtonProps}>
                Clear All
            </FlatButton>
        </>
    ) : null;

    const buttons = (
        <span
            ref={criteriaButtonsRef}
            className={classNames('strip-of-pills-group__buttons', {
                'strip-of-pills-group__buttons__overflow': isOutOfBounds,
            })}>
            {moreButton}
            {clearButton}
        </span>
    );

    const labelId = useId();

    const captionNode = label
        ? <span id={labelId} className={classes['strip-of-pills-group__caption']}>{`${label} (${count})`}</span>
        : null;

    return (
        <Scroll className={classes['strip-of-pills-group']} {...props}>
            <Group
                role="group"
                aria-labelledby={captionNode ? labelId : undefined}
                alignItems="center"
                gap="none"
                wrap={isCollapsed ? 'nowrap' : 'wrap'}>
                <div
                    ref={mergeRefs(scrollRef, pillContainerRef)}
                    className={classNames('strip-of-pills-group__overflow', {
                        'strip-of-pills-group__overflow_collapsed': isCollapsed,
                        'strip-of-pills-group__overflow_expanded': !isCollapsed,
                    })}>
                    {captionNode}
                    {children}
                    {!isCollapsed ? buttons : null}
                </div>
                {isCollapsed ? buttons : null}
            </Group>
        </Scroll>
    );
};
